import type {
  DashboardIOCSearchResult,
  DashboardQueryRequest,
  DashboardSearchProductIdsResult,
  FilterAstNode,
} from '@truss-security/truss-sdk-internal';
import {
  ArrayAttribute,
  AttributeCounts,
  IdSet,
  PgProduct,
  ProductAttribute,
  ScalarAttribute,
} from '../shared/types';
import { dashboardQuery, type PgQueryOptions } from './apiService';
import { getCachedData } from './cacheService';
import { queryCache } from './queryCache';

export type IOCSearchResult = DashboardIOCSearchResult;

export interface SearchProductIdsResult {
  count: number;
  productIds: number[];
  productIdsWithTimestamps: Array<{ id: number; timestamp: Date }>;
}

const TIME_SERIES_QUERY_TIMEOUT_MS = 60000;
const DEBUG_PG_QUERY_TRACE = false;
const QUERY_CONTEXT_MAX_AGE_MS = 10000;
let pgTraceSeq = 0;

interface QueryMetrics {
  queryName: string;
  duration: number;
  timestamp: Date;
  params?: unknown;
}

const queryMetrics: QueryMetrics[] = [];
const MAX_METRICS = 1000;

const getRecentUiQueryContext = (): Record<string, unknown> | null => {
  if (typeof window === 'undefined') return null;
  const ctx = (window as unknown as { __trussQueryContext?: { action?: string; timestamp?: number } })
    .__trussQueryContext;
  if (!ctx || typeof ctx.timestamp !== 'number') return null;
  if (Date.now() - ctx.timestamp > QUERY_CONTEXT_MAX_AGE_MS) return null;
  return ctx as Record<string, unknown>;
};

const toIso = (d: Date | null): string | null => (d ? d.toISOString() : null);

async function timedDashboardQuery<T>(
  body: DashboardQueryRequest,
  queryName?: string,
  options?: PgQueryOptions,
): Promise<T> {
  const startTime = performance.now();
  const queryId = queryName || 'unnamed';
  const traceId = ++pgTraceSeq;
  const uiContext = getRecentUiQueryContext();
  if (DEBUG_PG_QUERY_TRACE) {
    console.log(`[PG TRACE] start #${traceId} ${queryId}`, { traceId, queryId, hasUiContext: !!uiContext, uiContext });
  }
  try {
    const result = await dashboardQuery<T>(body, options);
    const duration = performance.now() - startTime;
    if (DEBUG_PG_QUERY_TRACE) {
      console.log(`[PG TRACE] success #${traceId} ${queryId}`, {
        traceId,
        queryId,
        durationMs: Math.round(duration),
        hasUiContext: !!uiContext,
      });
    }
    if (duration > 1000) {
      console.warn(`[SLOW QUERY] ${queryId} took ${duration.toFixed(2)}ms`);
    }
    queryMetrics.push({
      queryName: queryId,
      duration,
      timestamp: new Date(),
    });
    if (queryMetrics.length > MAX_METRICS) queryMetrics.shift();
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    if (DEBUG_PG_QUERY_TRACE) {
      console.error(`[PG TRACE] error #${traceId} ${queryId}`, {
        traceId,
        queryId,
        durationMs: Math.round(duration),
        error: error instanceof Error ? error.message : String(error),
      });
    }
    console.error(`[QUERY ERROR] ${queryId} failed after ${duration.toFixed(2)}ms:`, error);
    throw error;
  }
}

export interface ArrayAttributeCountParams {
  entityType: ArrayAttribute;
  fromDate: Date | null;
  toDate: Date | null;
  limit?: number;
  offset?: number;
  filterAst?: FilterAstNode | null;
  specificValues?: string[];
}

export interface ScalarAttributeCountParams {
  attributeName: ScalarAttribute;
  fromDate: Date | null;
  toDate: Date | null;
  limit?: number;
  offset?: number;
  filterAst?: FilterAstNode | null;
  specificValues?: string[];
}

export interface ArrayAttributeValueSearchParams {
  entityType: ArrayAttribute;
  search: string;
  fromDate: Date | null;
  toDate: Date | null;
  limit?: number;
  filterAst?: FilterAstNode | null;
}

export interface ScalarAttributeValueSearchParams {
  attributeName: ScalarAttribute;
  search: string;
  fromDate: Date | null;
  toDate: Date | null;
  limit?: number;
  filterAst?: FilterAstNode | null;
}

export interface IndicatorTotalCountsParams {
  fromDate: Date | null;
  toDate: Date | null;
  filterAst?: FilterAstNode | null;
}

export interface UniqueCountForAttributeParams {
  attribute: ProductAttribute;
  fromDate: Date | null;
  toDate: Date | null;
  filterAst?: FilterAstNode | null;
  indicatorType?: string;
}

export const getArrayAttributeCount = async (p: ArrayAttributeCountParams): Promise<AttributeCounts> => {
  const cacheKey = { ...p };
  const cached = queryCache.get<AttributeCounts>(`getArrayAttributeCount:${p.entityType}`, cacheKey);
  if (cached) return cached;
  const out = await timedDashboardQuery<AttributeCounts>(
    {
      operation: 'getArrayAttributeCount',
      input: {
        entityType: p.entityType,
        fromDate: toIso(p.fromDate),
        toDate: toIso(p.toDate),
        limit: p.limit,
        offset: p.offset,
        filterAst: p.filterAst,
        specificValues: p.specificValues,
      },
    },
    `getArrayAttributeCount:${p.entityType}`,
  );
  queryCache.set(`getArrayAttributeCount:${p.entityType}`, cacheKey, out);
  return out;
};

export const getArrayAttributeValueSearch = async (p: ArrayAttributeValueSearchParams): Promise<AttributeCounts> => {
  const cacheKey = { ...p };
  const cached = queryCache.get<AttributeCounts>(`getArrayAttributeValueSearch:${p.entityType}`, cacheKey);
  if (cached) return cached;
  const out = await timedDashboardQuery<AttributeCounts>(
    {
      operation: 'getArrayAttributeValueSearch',
      input: {
        entityType: p.entityType,
        search: p.search,
        fromDate: toIso(p.fromDate),
        toDate: toIso(p.toDate),
        limit: p.limit,
        filterAst: p.filterAst,
      },
    },
    `getArrayAttributeValueSearch:${p.entityType}`,
  );
  queryCache.set(`getArrayAttributeValueSearch:${p.entityType}`, cacheKey, out);
  return out;
};

export const getScalarAttributeCount = async (p: ScalarAttributeCountParams): Promise<AttributeCounts> => {
  const cacheKey = { ...p };
  const cached = queryCache.get<AttributeCounts>(`getScalarAttributeCount:${p.attributeName}`, cacheKey);
  if (cached) return cached;
  const out = await timedDashboardQuery<AttributeCounts>(
    {
      operation: 'getScalarAttributeCount',
      input: {
        attributeName: p.attributeName,
        fromDate: toIso(p.fromDate),
        toDate: toIso(p.toDate),
        limit: p.limit,
        offset: p.offset,
        filterAst: p.filterAst,
        specificValues: p.specificValues,
      },
    },
    `getScalarAttributeCount:${p.attributeName}`,
  );
  queryCache.set(`getScalarAttributeCount:${p.attributeName}`, cacheKey, out);
  return out;
};

export const getScalarAttributeValueSearch = async (p: ScalarAttributeValueSearchParams): Promise<AttributeCounts> => {
  const cacheKey = { ...p };
  const cached = queryCache.get<AttributeCounts>(`getScalarAttributeValueSearch:${p.attributeName}`, cacheKey);
  if (cached) return cached;
  const out = await timedDashboardQuery<AttributeCounts>(
    {
      operation: 'getScalarAttributeValueSearch',
      input: {
        attributeName: p.attributeName,
        search: p.search,
        fromDate: toIso(p.fromDate),
        toDate: toIso(p.toDate),
        limit: p.limit,
        filterAst: p.filterAst,
      },
    },
    `getScalarAttributeValueSearch:${p.attributeName}`,
  );
  queryCache.set(`getScalarAttributeValueSearch:${p.attributeName}`, cacheKey, out);
  return out;
};

export const getIndicatorTypeCountFilter = async (p: IndicatorTotalCountsParams): Promise<AttributeCounts> => {
  return timedDashboardQuery<AttributeCounts>(
    {
      operation: 'getIndicatorTypeCountFilter',
      input: { fromDate: toIso(p.fromDate), toDate: toIso(p.toDate), filterAst: p.filterAst },
    },
    'getIndicatorTypeCountFilter',
  );
};

export const getUniqueCountForAttribute = async (p: UniqueCountForAttributeParams): Promise<number> => {
  return timedDashboardQuery<number>(
    {
      operation: 'getUniqueCountForAttribute',
      input: {
        attribute: p.attribute,
        fromDate: toIso(p.fromDate),
        toDate: toIso(p.toDate),
        filterAst: p.filterAst,
        indicatorType: p.indicatorType,
      },
    },
    `getUniqueCountForAttribute:${p.attribute}`,
  );
};

export const getFilteredProductCount = async (
  fromDate: Date,
  toDate: Date,
  filterAst?: FilterAstNode | null,
): Promise<number> => {
  return timedDashboardQuery<number>(
    {
      operation: 'getFilteredProductCount',
      input: { fromDate: fromDate.toISOString(), toDate: toDate.toISOString(), filterAst: filterAst ?? undefined },
    },
    'getFilteredProductCount',
  );
};

export const getFilteredProductIdsWithCount = async (
  fromDate: Date,
  toDate: Date,
  page: number = 1,
  pageSize: number = 20,
  filterAst?: FilterAstNode | null,
): Promise<{ productIds: IdSet[]; totalCount: number }> => {
  const cacheKey = { filterAst, fromDate, toDate, page, pageSize };
  const cached = queryCache.get<{ productIds: IdSet[]; totalCount: number }>('getFilteredProductIdsWithCount', cacheKey);
  if (cached && cached.productIds && cached.productIds.length > 0) return cached;

  const raw = await timedDashboardQuery<{ productIds: IdSet[]; totalCount: number }>(
    {
      operation: 'getFilteredProductIdsWithCount',
      input: {
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        page,
        pageSize,
        filterAst: filterAst ?? undefined,
      },
    },
    'getFilteredProductIdsWithCount',
  );
  const result = {
    totalCount: raw.totalCount,
    productIds: raw.productIds.map((row) => {
      const tsRaw = row.timestamp as unknown;
      const tNum =
        typeof tsRaw === 'string' || typeof tsRaw === 'number'
          ? new Date(tsRaw).getTime()
          : tsRaw instanceof Date
            ? tsRaw.getTime()
            : 0;
      return { ...row, timestamp: tNum };
    }),
  };
  if (result.productIds.length > 0 || result.totalCount === 0) {
    queryCache.set('getFilteredProductIdsWithCount', cacheKey, result, { customTTL: 2 * 60 * 1000 });
  }
  return result;
};

function normalizePgProduct(row: Record<string, unknown>): PgProduct {
  const ts = row.timestamp;
  let timestamp: Date;
  if (ts instanceof Date) {
    timestamp = ts;
  } else if (typeof ts === 'string' || typeof ts === 'number') {
    timestamp = new Date(ts);
  } else {
    timestamp = new Date();
  }
  const pub = row.pub_date;
  const pub_date =
    pub == null ? null : pub instanceof Date ? pub : new Date(pub as string | number);
  return { ...(row as unknown as PgProduct), timestamp, pub_date };
}

export const getProducts = async (
  productIds: number[],
  page: number,
  pageSize: number,
): Promise<{ products: PgProduct[]; totalCount: number }> => {
  const raw = await timedDashboardQuery<{ products: Record<string, unknown>[]; totalCount: number }>(
    { operation: 'getProducts', input: { productIds, page, pageSize } },
    `getProducts:${productIds.length}items`,
  );
  return {
    products: raw.products.map((r) => normalizePgProduct(r)),
    totalCount: raw.totalCount,
  };
};

export const findSimilarProductsByTags = async (
  sourceProductIds: number[],
  filterAst: FilterAstNode | null | undefined,
  fromDate: Date,
  toDate: Date,
  limit: number = 20,
  requireAllProducts: boolean = false,
): Promise<PgProduct[]> => {
  const cacheKey = {
    sourceProductIds: sourceProductIds.sort().join(','),
    filterAst,
    fromDate,
    toDate,
    limit,
    requireAllProducts,
  };
  const cached = queryCache.get<PgProduct[]>('findSimilarProductsByTags', cacheKey);
  if (cached) return cached;
  const raw = await timedDashboardQuery<Record<string, unknown>[]>(
    {
      operation: 'findSimilarProductsByTags',
      input: {
        sourceProductIds,
        filterAst: filterAst ?? undefined,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        limit,
        requireAllProducts,
      },
    },
    'findSimilarProductsByTags',
  );
  const products = raw.map((r) => normalizePgProduct(r));
  queryCache.set('findSimilarProductsByTags', cacheKey, products, { customTTL: 5 * 60 * 1000 });
  return products;
};

export const getCategoryTimeSeries = async (forceRefresh: boolean = false) => {
  return getCachedData('category_time_series', async () => {
    return timedDashboardQuery<unknown[]>({ operation: 'getCategoryTimeSeries', input: {} }, 'getCategoryTimeSeries');
  }, forceRefresh);
};

export const getTrendGaugeTimeSeriesData = async (
  fromDate: Date,
  toDate: Date,
  filterAst?: FilterAstNode | null,
  timeCount: 'days' | 'hours' | 'minutes' = 'days',
): Promise<Array<{ timestamp: string; category: string; count: number }>> => {
  return timedDashboardQuery(
    {
      operation: 'getTrendGaugeTimeSeriesData',
      input: {
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        filterAst: filterAst ?? undefined,
        timeCount,
      },
    },
    `getTrendGaugeTimeSeriesData:${timeCount}`,
    { timeout: TIME_SERIES_QUERY_TIMEOUT_MS },
  );
};

export const getTimeSeriesByDateRange = async (
  attribute: 'category' | 'region' | 'industry' | 'source' | 'author' | 'tags',
  fromDate: Date,
  toDate: Date,
  filterAst?: FilterAstNode | null,
  timeCount: 'days' | 'hours' | 'minutes' = 'days',
) => {
  return timedDashboardQuery(
    {
      operation: 'getTimeSeriesByDateRange',
      input: {
        attribute,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        filterAst: filterAst ?? undefined,
        timeCount,
      },
    },
    `getTimeSeriesByDateRange:${attribute}:${timeCount}`,
    { timeout: TIME_SERIES_QUERY_TIMEOUT_MS },
  );
};

export const getRegionTimeSeries = async (forceRefresh: boolean = false) => {
  return getCachedData('region_time_series', async () => {
    return timedDashboardQuery<unknown[]>({ operation: 'getRegionTimeSeries', input: {} }, 'getRegionTimeSeries');
  }, forceRefresh);
};

export const getIndustryTimeSeries = async (forceRefresh: boolean = false) => {
  return getCachedData('industry_time_series', async () => {
    return timedDashboardQuery<unknown[]>({ operation: 'getIndustryTimeSeries', input: {} }, 'getIndustryTimeSeries');
  }, forceRefresh);
};

export const getSourceTimeSeries = async (forceRefresh: boolean = false) => {
  return getCachedData('source_time_series', async () => {
    return timedDashboardQuery<unknown[]>({ operation: 'getSourceTimeSeries', input: {} }, 'getSourceTimeSeries');
  }, forceRefresh);
};

export const getAuthorTimeSeries = async (forceRefresh: boolean = false) => {
  return getCachedData('author_time_series', async () => {
    return timedDashboardQuery<unknown[]>({ operation: 'getAuthorTimeSeries', input: {} }, 'getAuthorTimeSeries');
  }, forceRefresh);
};

export const getTagTimeSeries = async (forceRefresh: boolean = false) => {
  return getCachedData('tag_time_series', async () => {
    return timedDashboardQuery<unknown[]>({ operation: 'getTagTimeSeries', input: {} }, 'getTagTimeSeries');
  }, forceRefresh);
};

export interface IOCSearchParams {
  iocValue: string;
  fromDate?: Date;
  toDate?: Date;
  limit?: number;
  offset?: number;
}

export const searchIOC = async (p: IOCSearchParams): Promise<IOCSearchResult[]> => {
  return timedDashboardQuery<IOCSearchResult[]>(
    {
      operation: 'searchIOC',
      input: {
        iocValue: p.iocValue,
        fromDate: p.fromDate?.toISOString(),
        toDate: p.toDate?.toISOString(),
        limit: p.limit,
        offset: p.offset,
      },
    },
    `searchIOC:${p.iocValue.substring(0, 20)}`,
  );
};

export const searchProductIdsByValue = async (p: {
  searchValue: string;
  parentType: 'IOC' | 'author' | 'region' | 'industry' | 'tags' | 'category' | 'productId' | 'title';
  fromDate?: Date;
  toDate?: Date;
}): Promise<SearchProductIdsResult> => {
  const raw = await timedDashboardQuery<DashboardSearchProductIdsResult>(
    {
      operation: 'searchProductIdsByValue',
      input: {
        searchValue: p.searchValue,
        parentType: p.parentType,
        fromDate: p.fromDate?.toISOString(),
        toDate: p.toDate?.toISOString(),
      },
    },
    'searchProductIdsByValue',
  );
  return {
    count: raw.count,
    productIds: raw.productIds,
    productIdsWithTimestamps: raw.productIdsWithTimestamps.map((row) => ({
      id: row.id,
      timestamp: new Date(row.timestamp),
    })),
  };
};

export const getCrossTabulatedData = async (p: {
  xAttribute: ArrayAttribute;
  yAttribute: ArrayAttribute;
  fromDate: Date | null;
  toDate: Date | null;
  limit?: number;
  filterAst?: FilterAstNode | null;
}): Promise<{ [xValue: string]: { [yValue: string]: number } }> => {
  return timedDashboardQuery(
    {
      operation: 'getCrossTabulatedData',
      input: {
        xAttribute: p.xAttribute,
        yAttribute: p.yAttribute,
        fromDate: toIso(p.fromDate),
        toDate: toIso(p.toDate),
        limit: p.limit,
        filterAst: p.filterAst,
      },
    },
    `getCrossTabulatedData:${p.xAttribute}-${p.yAttribute}`,
  );
};
